import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../services/api/client';
import AuthLayout from '../components/layout/AuthLayout';
import { Button, Text } from '../components/ui';
import { Input } from '../components/forms';

const resetSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string(),
}).refine(data => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type ResetFormData = z.infer<typeof resetSchema>;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  const mutation = useMutation({
    mutationFn: async (data: ResetFormData) => {
      const response = await apiClient.post('/auth/password-reset/confirm', {
        token,
        new_password: data.password,
      });
      return response.data;
    },
    onSuccess: () => {
      setDone(true);
    },
  });

  if (!token) {
    return (
      <AuthLayout title="Invalid Link" subtitle="This password reset link is missing a token">
        <div className="text-center">
          <Text className="text-gray-600 mb-4">
            Please check the link from your email or request a new reset.
          </Text>
          <Button variant="primary" fullWidth onClick={() => navigate('/forgot-password')}>
            Request New Link
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password Reset" subtitle="Your password has been updated">
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-4" />
          <Text className="text-gray-600 mb-6">
            You can now log in with your new password.
          </Text>
          <Button variant="primary" fullWidth onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset Password" subtitle="Enter your new password">
      <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
        <div className="relative">
          <Input
            {...register('password')}
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            error={errors.password?.message}
            autoFocus
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-gray-500 hover:text-gray-700"
            tabIndex={-1}
          >
            {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
          </button>
        </div>

        <Input
          {...register('confirm_password')}
          label="Confirm Password"
          type="password"
          error={errors.confirm_password?.message}
        />

        {mutation.isError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <Text className="text-sm text-red-600">
              Invalid or expired reset token. Please request a new reset link.
            </Text>
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          isLoading={mutation.isPending}
        >
          Reset Password
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
